import { useCallback, useState } from 'react';
import { useTimeclockStore, type TimeClockEventKey, type WorkLocation } from '@/store/useTimeclockStore';
import { useVehicleStore, type VehicleDto } from '@/store/useVehicleStore';
import { useMyVehicleTripsStore } from '@/store/useMyVehicleTripsStore';
import { getMePlanning } from '@/api/generated/me-planning/me-planning';
import { isoDate } from '@/utils/dateUtils';
import { DRIVER, initialTripChoice, skipVehicleQuestion, type PlannedTrip, type TripChoice, type TripDialogMode } from '@/utils/vehicleTrips';
import { useTripDialogs } from '@/hooks/useTripDialogs';

/** Stamps that may start or end a trip — breaks never do. */
export type VehicleStampEvent = Extract<TimeClockEventKey, 'clockIn' | 'clockOut'>;

/** What the caller already knows about this stamp. */
export interface VehicleStampOptions {
  /** Sent along with the stamp (clock-in only). */
  workLocation?: WorkLocation;
  /** The stretch uses no vehicle (home office): clock in without the trip question. */
  noVehicle?: boolean;
}

/** Vehicle and role the administration planned for the day (FACF-118), if any. */
async function plannedTripOf(date: string): Promise<PlannedTrip | null> {
  try {
    const entries = await getMePlanning().getApiV1MePlanning({ from: date, to: date });
    const planned = entries.find((e) => e.date === date && e.plannedDriverRole);
    return planned ? { driverRole: planned.plannedDriverRole, vehicleId: planned.plannedVehicleId ?? null } : null;
  } catch {
    // The plan only preselects — without it the employee simply picks.
    return null;
  }
}

async function activeVehicles(): Promise<VehicleDto[]> {
  try {
    return await useVehicleStore.getState().fetchActiveVehicles();
  } catch {
    // Without the list the trip question is skipped; the stamp itself does not need a vehicle.
    return [];
  }
}

/**
 * Everything the trip dialog starts from: active vehicles, the running trip, whether a vehicle
 * still has to be parked and the preselection (planning, otherwise "Kein Fahrer"/"Fahrer").
 * Start, destination and purpose the dialog asks for itself, per chosen vehicle.
 */
export async function prepareTripStart(mode: TripDialogMode, date: string) {
  const trips = useMyVehicleTripsStore.getState();
  const [vehicles, current, unparked, planned] = await Promise.all([
    activeVehicles(),
    trips.fetchCurrent(),
    trips.fetchMyUnparked(),
    plannedTripOf(date),
  ]);
  const initial: TripChoice = initialTripChoice({
    mode,
    planned,
    activeVehicleIds: vehicles.map((v) => v.id ?? ''),
    driverBlocked: unparked.length > 0,
  });
  return { vehicles, current, initial };
}

/**
 * Wraps clock-in and clock-out with the trip questions (FAFD-68, ADR-0027). The trip is its own
 * process; the stamp only carries it along:
 *
 * - Clock-in: no question while an own trip or ride runs, in home office or without vehicles.
 *   Otherwise TripStartDialog with "Kein Fahrer" preselected (or the planned role); "Fahrer"
 *   or "Beifahrer" starts the trip together with the clock-in.
 * - Clock-out: with an own running trip as driver, EndTripDialog offers "Ausstempeln und Fahrt
 *   beenden" or "Nur ausstempeln". A ride ends with the clock-out on the server, without asking.
 *
 * Pages don't use it directly — useClockStamp asks the work location first and then calls this.
 * `stamp` resolves once the stamp is recorded or a dialog was cancelled, and rethrows the
 * store's error like recordEvent does.
 */
export function useVehicleStamp() {
  const recordEvent = useTimeclockStore((s) => s.recordEvent);
  const dialogs = useTripDialogs();
  const { askStart, askEnd } = dialogs;
  const [preparing, setPreparing] = useState(false);

  const stamp = useCallback(
    async (eventType: VehicleStampEvent, date?: string, timestamp?: string, options: VehicleStampOptions = {}) => {
      const { workLocation, noVehicle = false } = options;
      const refresh = () => useMyVehicleTripsStore.getState().refreshAfterStamp();

      if (eventType === 'clockOut') {
        setPreparing(true);
        const current = await useMyVehicleTripsStore.getState().fetchCurrent().finally(() => setPreparing(false));
        if (current?.role !== DRIVER) {
          await recordEvent(eventType, date, timestamp);
          refresh();
          return;
        }
        const answer = await askEnd('clockOut', current);
        if (answer === null) return;
        await recordEvent(eventType, date, timestamp, answer.endTrip ? { endTrip: answer.endTrip } : undefined);
        refresh();
        return;
      }

      setPreparing(true);
      const prepared = await prepareTripStart('clockIn', date ?? isoDate(new Date())).finally(() =>
        setPreparing(false),
      );
      const skip = skipVehicleQuestion({
        hasRunningTrip: prepared.current !== null,
        homeOffice: noVehicle,
        activeVehicleCount: prepared.vehicles.length,
      });
      if (skip) {
        await recordEvent(eventType, date, timestamp, undefined, workLocation);
        return;
      }
      const answer = await askStart({
        mode: 'clockIn',
        vehicles: prepared.vehicles,
        initial: prepared.initial,
        departureAt: timestamp,
        notClockedIn: false,
      });
      if (answer === null) return;
      await recordEvent(eventType, date, timestamp, answer.trip ? { trip: answer.trip } : undefined, workLocation);
      if (answer.trip) refresh();
    },
    [askEnd, askStart, recordEvent],
  );

  return { stamp, preparing, startDialog: dialogs.startDialog, endDialog: dialogs.endDialog };
}
